'use client'
import logger from '@achieve/cx-logger'
import { useEffect } from 'react'

const RELOAD_FLAG = 'chunkErrorReloadAttempted'

/**
 * Patterns that identify a chunk-load failure. Covers:
 *   - Next.js / webpack lazy-chunk rejections   ("Loading chunk N failed")
 *   - CSS chunk failures                        ("CSS chunk N")
 *   - Generic webpack ChunkLoadError class name ("ChunkLoadError")
 *   - Vite / native ESM dynamic-import errors   ("failed to fetch dynamically imported module")
 */
const CHUNK_ERROR_PATTERNS = [
  'Loading chunk',
  'CSS chunk',
  'ChunkLoadError',
  'failed to fetch dynamically imported module',
]

function isChunkError(message: string): boolean {
  const lower = message.toLowerCase()
  return CHUNK_ERROR_PATTERNS.some((pattern) => lower.includes(pattern.toLowerCase()))
}

function attemptReload(): void {
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    // Already tried once this tab session — avoid an infinite loop.
    logger.error(
      '[ChunkErrorRecovery] Chunk load error persists after reload. Manual hard refresh required.'
    )
    return
  }
  sessionStorage.setItem(RELOAD_FLAG, '1')

  // `window.location.reload()` may serve stale HTML from the browser's HTTP
  // cache, which would still reference the old chunk hashes and loop.
  // Instead we:
  //   1. Clear the Cache Storage API (SW / Next.js fetch caches).
  //   2. Navigate to the same URL with a timestamp query param so the browser
  //      treats it as a new URL, bypassing the HTTP document cache entirely —
  //      equivalent to a Shift+Cmd+R hard refresh.
  const doReload = async (): Promise<void> => {
    if ('caches' in window) {
      try {
        const cacheKeys = await caches.keys()
        await Promise.all(cacheKeys.map((key) => caches.delete(key)))
      } catch (error) {
        // Cache clearing failed — proceed with navigation anyway.
        logger.error('caches in window found an error: ', { error })
      }
    }
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map((r) => r.unregister()))
      } catch (error) {
        // serviceWorker clearing failed — proceed with navigation anyway.
        logger.error('serviceWorker found an error: ', { error })
      }
    }
    const url = new URL(window.location.href)
    url.searchParams.set('_cb', Date.now().toString())
    window.location.replace(url.toString())
  }
  doReload()
}

/**
 * Global chunk-load error recovery hook.
 *
 * Attaches two listeners to `window`:
 *   1. `error`               — catches ErrorEvent from failed <script>/<link> tags
 *   2. `unhandledrejection`  — catches dynamic import() Promise rejections
 *
 * On a chunk failure it performs a single automatic `window.location.reload()`.
 * A `sessionStorage` flag prevents infinite reload loops within the same tab session.
 */
export function useChunkErrorRecovery(): void {
  useEffect(() => {
    // Strip the cache-bust param (_cb) added by attemptReload() so it never
    // lingers in the address bar after a successful chunk-error recovery.
    const url = new URL(window.location.href)
    if (url.searchParams.has('_cb')) {
      url.searchParams.delete('_cb')
      window.history.replaceState(null, '', url.toString())
    }

    function handleError(event: ErrorEvent): void {
      const message = event?.message ?? ''
      if (isChunkError(message)) {
        logger.error('[Chunk handle error handleError]', { message, event })
        event.preventDefault()
        attemptReload()
      }
    }

    function handleUnhandledRejection(event: PromiseRejectionEvent): void {
      const reason = event?.reason
      const message: string =
        (reason instanceof Error ? reason.message : String(reason ?? '')) ?? ''
      if (isChunkError(message)) {
        logger.error('[Chunk handle error handleUnhandledRejection]', { message, event })
        event.preventDefault()
        attemptReload()
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])
}
